(function () {
  const ACCORDION_SELECTOR = '.foerg-accordeon';
  const TRIGGER_SELECTOR = 'h4';
  const PANEL_SELECTOR = '.accordeon_content';
  const WRAPPER_SELECTOR = '.block-wrapper';
  const DATA_READY_ATTR = 'foergAccordionReady';
  const HEIGHT_PROPERTY = 'height';
  const TRANSITION_FALLBACK_MS = 600;

  const instances = new WeakMap();
  const registry = new Set();
  let resizeAttached = false;
  const motionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  const prefersReducedMotion = () =>
    Boolean(motionQuery && motionQuery.matches);

  const uniqueId = (() => {
    let seed = Date.now();
    return () => 'foerg-accordion-' + seed++;
  })();

  function onHeightTransitionEnd(element, callback) {
    let finished = false;
    let timeoutId = null;

    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      element.removeEventListener('transitionend', handleTransitionEnd);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      callback();
    };

    const handleTransitionEnd = (event) => {
      if (event.target === element && event.propertyName === HEIGHT_PROPERTY) {
        finish();
      }
    };

    element.addEventListener('transitionend', handleTransitionEnd);
    timeoutId = window.setTimeout(finish, TRANSITION_FALLBACK_MS);

    return finish;
  }

  function ensureResizeListener() {
    if (resizeAttached) {
      return;
    }
    window.addEventListener('resize', () => {
      registry.forEach((instance) => {
        instance.items.forEach((item) => {
          if (!item.open) {
            return;
          }
          if (item.panel.style.height === 'auto') {
            return;
          }
          item.panel.style.height = item.panel.scrollHeight + 'px';
        });
      });
    });
    resizeAttached = true;
  }

  function cancelTransition(item) {
    if (item.cleanup) {
      item.cleanup();
      item.cleanup = null;
    }
  }

  function resetItem(item) {
    cancelTransition(item);
    item.open = false;
    item.trigger.classList.remove('select');
    item.trigger.setAttribute('aria-expanded', 'false');
    item.panel.setAttribute('aria-hidden', 'true');
    item.panel.classList.remove('selected-content');
    if (item.wrapper) {
      item.wrapper.classList.remove('selected-block');
    }
    item.panel.style.height = '0px';
  }

  function closeItem(item, animate) {
    if (!item.open) {
      resetItem(item);
      return;
    }
    cancelTransition(item);
    item.open = false;
    item.trigger.classList.remove('select');
    item.trigger.setAttribute('aria-expanded', 'false');
    item.panel.setAttribute('aria-hidden', 'true');

    const wrapper = item.wrapper;
    const panel = item.panel;

    if (!animate) {
      resetItem(item);
      return;
    }

    const startHeight = panel.scrollHeight;
    panel.style.height = startHeight + 'px';
    panel.offsetHeight; // force reflow for transition
    panel.style.height = '0px';

    item.cleanup = onHeightTransitionEnd(panel, () => {
      panel.classList.remove('selected-content');
      if (wrapper) {
        wrapper.classList.remove('selected-block');
      }
      item.cleanup = null;
    });
  }

  function openItem(item, animate) {
    if (item.open) {
      if (!animate) {
        item.panel.style.height = 'auto';
      }
      return;
    }
    cancelTransition(item);
    item.open = true;
    item.trigger.classList.add('select');
    item.trigger.setAttribute('aria-expanded', 'true');
    item.panel.setAttribute('aria-hidden', 'false');
    item.panel.classList.add('selected-content');
    if (item.wrapper) {
      item.wrapper.classList.add('selected-block');
    }

    if (!animate) {
      item.panel.style.height = 'auto';
      return;
    }

    item.panel.style.height = item.panel.scrollHeight + 'px';
    item.cleanup = onHeightTransitionEnd(item.panel, () => {
      item.panel.style.height = 'auto';
      item.cleanup = null;
    });
  }

  function closeAll(instance, options) {
    const animate =
      options.animate === undefined ? true : Boolean(options.animate);
    const except = options.except || null;
    instance.items.forEach((item) => {
      if (item === except) {
        return;
      }
      closeItem(item, animate);
    });
  }

  function toggleItem(instance, item, event) {
    event.preventDefault();
    const animate = !prefersReducedMotion();
    if (item.open) {
      closeItem(item, animate);
      return;
    }
    closeAll(instance, { animate, except: item });
    openItem(item, animate);
  }

  function getPanelFromTrigger(trigger) {
    let panel = null;
    let wrapper = null;

    let sibling = trigger.nextElementSibling;
    if (sibling) {
      if (sibling.classList.contains('accordeon_content')) {
        panel = sibling;
      } else if (sibling.classList.contains('block-wrapper')) {
        wrapper = sibling;
        panel = sibling.querySelector(PANEL_SELECTOR);
      }
    }

    if (!panel && trigger.parentElement) {
      const parent = trigger.parentElement;
      if (parent.classList.contains('block-wrapper')) {
        wrapper = parent;
        panel = parent.querySelector(PANEL_SELECTOR);
      }
    }

    if (!panel && trigger.parentElement) {
      panel = trigger.parentElement.querySelector(PANEL_SELECTOR);
      if (panel && panel.parentElement.classList.contains('block-wrapper')) {
        wrapper = panel.parentElement;
      }
    }

    if (panel && !panel.classList.contains('accordeon_content')) {
      panel = null;
      wrapper = null;
    }

    return { panel, wrapper };
  }

  function bindTrigger(instance, item) {
    const handler = (event) => toggleItem(instance, item, event);
    const keyHandler = (event) => {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Spacebar'
      ) {
        event.preventDefault();
        toggleItem(instance, item, event);
      }
    };
    item.trigger.addEventListener('click', handler);
    item.trigger.addEventListener('keydown', keyHandler);
    item.destroyHandlers = () => {
      item.trigger.removeEventListener('click', handler);
      item.trigger.removeEventListener('keydown', keyHandler);
    };
  }

  function buildItems(accordion, idPrefix) {
    const triggers = Array.from(
      accordion.querySelectorAll(TRIGGER_SELECTOR)
    ).filter((trigger) => trigger.closest(ACCORDION_SELECTOR) === accordion);

    const items = [];

    triggers.forEach((trigger, index) => {
      const { panel, wrapper } = getPanelFromTrigger(trigger);
      if (!panel) {
        return;
      }

      if (!trigger.id) {
        trigger.id = idPrefix + '-trigger-' + index;
      }
      if (!panel.id) {
        panel.id = idPrefix + '-panel-' + index;
      }

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', 'false');
      if (!trigger.hasAttribute('tabindex')) {
        trigger.setAttribute('tabindex', '0');
      }

      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', trigger.id);
      panel.setAttribute('aria-hidden', 'true');

      items.push({
        trigger,
        panel,
        wrapper: wrapper || null,
        open: false,
        cleanup: null,
        destroyHandlers: null,
      });
    });

    return items;
  }

  function applyInitialState(instance) {
    let defaultItem =
      instance.items.find(
        (item) =>
          item.trigger.classList.contains('select') ||
          item.panel.classList.contains('selected-content') ||
          (item.wrapper && item.wrapper.classList.contains('selected-block'))
      ) || null;

    instance.items.forEach((item) => {
      resetItem(item);
    });

    if (defaultItem) {
      openItem(defaultItem, false);
    }
  }

  function initAccordion(accordion) {
    if (accordion.dataset[DATA_READY_ATTR] === '1') {
      return;
    }

    const idPrefix = uniqueId();
    const items = buildItems(accordion, idPrefix);

    if (!items.length) {
      return;
    }

    const instance = { accordion, items };
    items.forEach((item) => bindTrigger(instance, item));
    applyInitialState(instance);

    accordion.dataset[DATA_READY_ATTR] = '1';
    instances.set(accordion, instance);
    registry.add(instance);
    ensureResizeListener();
  }

  function initAll() {
    document
      .querySelectorAll(ACCORDION_SELECTOR)
      .forEach((accordion) => initAccordion(accordion));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();
